import { useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';

import LoginForm from '../../components/auth/LoginForm';
import LogoCarousel from '../../components/shared/LogoCarousel';
import { useAuth } from '../../context/AuthContext';

const Login = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Obtener URL de redirección de los parámetros
  const redirectUrl = searchParams.get('redirect') || '/dashboard';

  // Si el usuario ya está autenticado, redirigir
  useEffect(() => {
    if (user) {
      navigate(redirectUrl);
    }
  }, [user, navigate, redirectUrl]);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <div className="flex flex-1 flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          {/* Logo NFCores principal */}
          <div className="flex justify-center mb-8">
            <Link to="/" className="flex items-center space-x-3">
              <img
                className="h-12 w-auto"
                src="/logo.svg"
                alt="NFCores Logo"
                onError={(e) => {
                  e.currentTarget.src = '/assets/images/NFCores-original.png';
                }}
              />
              <div className="text-3xl font-bold text-primary-600">NFCores</div>
            </Link>
          </div>

          {/* Título */}
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Inicia sesión en tu cuenta
            </h2>
            <p className="text-gray-600">
              Accede a tu panel de control para gestionar tus pulseras SOS NFC
            </p>
          </div>
          
          {/* Formulario de login */}
          <LoginForm redirectUrl={redirectUrl} />

          {/* Enlaces */}
          <div className="mt-6 text-center space-y-2">
            <p className="text-sm text-gray-600">
              ¿No tienes cuenta?{' '}
              <Link to="/auth/register" className="font-medium text-primary-600 hover:text-primary-500">
                Regístrate aquí
              </Link>
            </p>
          </div>
        </div>
      </div>
      
      {/* Panel lateral con carrusel */}
      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 h-full w-full bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700">
          <div className="flex h-full flex-col items-center justify-center p-10 text-center">
            {/* Información principal */}
            <div className="max-w-md mb-12">
              <h2 className="text-4xl font-bold text-white mb-6">
                Acceso rápido a información vital
              </h2>
              <p className="text-xl text-primary-100 leading-relaxed">
                NFCores proporciona información médica crítica al instante mediante dispositivos NFC.
                Una solución simple y efectiva para salvar vidas.
              </p>
            </div>

            {/* Carrusel de logos de submarcas */}
            <div className="w-full max-w-sm">
              <LogoCarousel autoPlay={true} interval={4000} />
            </div>

            {/* Características destacadas */}
            <div className="mt-12 grid grid-cols-2 gap-6 text-center">
              <div className="space-y-2">
                <span className="text-3xl" role="img" aria-label="rayo">⚡</span>
                <p className="text-sm text-white font-medium">Acceso instantáneo</p>
              </div>
              <div className="space-y-2">
                <span className="text-3xl" role="img" aria-label="hospital">🏥</span>
                <p className="text-sm text-white font-medium">Información médica</p>
              </div>
              <div className="space-y-2">
                <span className="text-3xl" role="img" aria-label="teléfono móvil">📱</span>
                <p className="text-sm text-white font-medium">Tecnología NFC</p>
              </div>
              <div className="space-y-2">
                <span className="text-3xl" role="img" aria-label="candado cerrado">🔐</span>
                <p className="text-sm text-white font-medium">Datos seguros</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
